/**
 * The composer's slowmode bar (2026-10-29): attached to its top edge, as
 * the reply bar is. It says how often you may send, and counts down after
 * a send ("You can send again in 0:07"). Nothing shows where slowmode is
 * off or doesn't apply to you (Manage Messages, Manage Channels, Bypass
 * Slowmode), or when the console turned slowmode off.
 *
 * Console v2 (§3.4): while raid mode runs, a public text channel's delay is
 * at least raid mode's floor (Manage Messages exempt), and the bar says the
 * effective one, and why.
 */
import type { Channel } from '@neosocial/protocol';
import { useT } from '../i18n';
import { useConfig, useFlag } from '../store/config';
import { useData } from '../store/data';
import { countdownText, slowmodeExempt, slowmodeLabel } from '../store/slowmode';
import { Icon } from '../ui/kit';
import { raidFloor } from './raidSlowmode';

export function SlowmodeBar({ channel, left }: { channel: Channel; left: number }) {
  const { t } = useT();
  const on = useFlag('slowmode');
  const own = on ? (channel.rate_limit_per_user ?? 0) : 0;
  const ownExempt = useData((d) => own > 0 && slowmodeExempt(d, channel));
  const raid = useConfig((c) => c.config?.raid);
  const floor = useData((d) => raidFloor(d, channel, raid));
  const ownRate = ownExempt ? 0 : own;
  const rate = Math.max(ownRate, floor);
  const fromRaid = floor > ownRate;
  return (
    <div
      className="cmp-bar ns-slow-bar"
      role="status"
      data-waiting={left > 0 || undefined}
      data-raid={fromRaid || undefined}
    >
      <Icon name={fromRaid ? 'shield' : 'clock'} size="s" />
      <span className="ns-slow-t">
        {left > 0
          ? t('sm.wait', { time: countdownText(left) })
          : fromRaid
            ? t('gov.sm.raid', { time: slowmodeLabel(t, rate) })
            : t('sm.every', { time: slowmodeLabel(t, rate) })}
      </span>
    </div>
  );
}
